import Card from '@/components/elements/Card/Card';
import EmptyState from '@/components/templates/EmptyState';
import Reviews from '@/features/reviews/Reviews';
import { ReviewsHeader } from '@/features/reviews/ReviewsHeader';
import { MINIMUM_AMOUNT_OF_REVIEWS_REQUIRED } from '@/constants/reviewConstants';
import useMobileView from '@/hooks/useMobileView';
import useReviews, { REVIEWS_PAGE_LIMIT } from '@/hooks/useReviews';
import { _s } from '@/locale';
import { TIME_FILTER } from '@/constants/timeConstants';
import type { SelectedTimeFilterOptions } from '@/types/reviews';
import { Place } from '@/types/state/shared';
import { useRef } from 'react';
import { DealDetailItem } from '../types/dealDetails';

type DealSectionReviewsProps = {
  deal: DealDetailItem | null;
  loading?: boolean;
  reviewsRef?: React.RefObject<HTMLDivElement>;
  reviewsData: Place['reviews'] | null;
  isFetching: boolean;
};

export const DealSectionReviews = ({ deal, loading, reviewsRef, reviewsData, isFetching }: DealSectionReviewsProps) => {
  const { isMobileView } = useMobileView();
  const tooltipTargetRef = useRef<HTMLDivElement>(null);

  const uid = deal?.place?.id ? `${deal.place.id}_deal` : undefined;
  const initialSearchFilter = {
    placeId: deal?.place?.id,
    page: 1,
    limit: REVIEWS_PAGE_LIMIT,
    isMpReviews: true,
    hasMessage: true,
    timeFilter: TIME_FILTER.FOREVER as SelectedTimeFilterOptions,
  };

  const { setRatingFilter } = useReviews({
    uid,
    searchFilter: initialSearchFilter,
    initiateState: true,
    originType: 'dealsPage',
  });

  const displayReviews = !!deal?.place?.settings?.displayReviews;

  if (loading || !deal || !displayReviews) {
    return null;
  }

  const haveReviews = Boolean((reviewsData?.stats?.count ?? 0) >= MINIMUM_AMOUNT_OF_REVIEWS_REQUIRED);

  if (isFetching && !reviewsData) {
    return (
      <Card size={isMobileView ? 'lg' : 'xl'}>
        <div className="gap-2xl flex flex-col">
          <ReviewsHeader className={'mb-lg'} />
          <div className="space-y-md">
            <div className="bg-bg_secondary h-6 w-32 animate-pulse rounded" />
            <div className="bg-bg_secondary h-4 w-full animate-pulse rounded" />
            <div className="bg-bg_secondary h-4 w-3/4 animate-pulse rounded" />
          </div>
        </div>
      </Card>
    );
  }

  const placeForReviews = {
    id: deal.place.id,
    about: {
      name: deal.place.name,
      settings: { subscriptionType: deal.place.settings?.subscriptionType ?? undefined },
    },
    reviews: reviewsData ?? {},
  } as unknown as Place;

  return (
    <>
      <div className="relative" ref={tooltipTargetRef}></div>
      <div id="reviews" ref={reviewsRef} className="scroll-mt-[144px]">
        {haveReviews && (
          <>
            <Card size={isMobileView ? 'lg' : 'xl'}>
              <Reviews
                uid={uid}
                placeId={placeForReviews.id}
                hideStaff={false}
                isMpReviews={true}
                tooltipTargetRef={tooltipTargetRef}
                focus={'rating'}
                onRatingClick={setRatingFilter}
              />
            </Card>
          </>
        )}
        {!haveReviews && (
          <Card size={isMobileView ? 'lg' : 'xl'}>
            <div className="gap-2xl flex flex-col">
              <ReviewsHeader className={'mb-lg'} />
              <EmptyState title={_s('notEnoughReviews')} icon={<img src="/images/illustrations/empty.svg" alt="" />} />
            </div>
          </Card>
        )}
      </div>
    </>
  );
};
